import * as React from 'react';
import { Title } from '../../../title';
import { I18n } from 'react-redux-i18n';
import { Subtitle } from '../../../subtitle';
import { Checkbox } from '../../../checkbox';
import { ConditionalField } from '../../../conditional-field';
import { FolderPicker } from '../../../folder-picker';
import { FolderPrimaryListDto } from '@common/dto/folder/primary-list-dto';
import { Button } from '@common/components/Button';
import { RiskSettings } from '../../../risk-settings';
import { FieldLayout } from '../../../field';
import { getTestAttributes } from '@common/utils/tests';
import { MODULE_NAME } from '@modules/fhq-design-tool/constants/shared';
import { QuestionnaireUpdateLocalDto } from '@modules/fhq-design-tool/dto/questionnaire-update-local-dto';
import { QuestionnaireRiskSettings } from '@common/dto/questionnaire/risk-settings';
import { QuestionnaireManagementErrors } from '@modules/fhq-design-tool/controllers/questionnaire-validation';
import { QuestionnaireCreateLocalDto } from '@modules/fhq-design-tool/dto/questionnaire-create-local-dto';

import './submitted-questionnaires.styles.scss';

export type SubmittedQuestionnairesProps = {
  errors?: QuestionnaireManagementErrors;
  folders: Array<FolderPrimaryListDto>;
  questionnaire: QuestionnaireUpdateLocalDto | QuestionnaireCreateLocalDto;
  onChange: (questionnaire: QuestionnaireUpdateLocalDto | QuestionnaireCreateLocalDto) => void;
};

export const SubmittedQuestionnaires: React.FC<SubmittedQuestionnairesProps> = (props) => {
  const { onChange, questionnaire, folders, errors } = props;
  const [riskSettingsOpened, setRiskSettingsOpened] = React.useState(false);

  const handleLockOnSubmitChange = React.useCallback((val: boolean) => {
    onChange({
      ...questionnaire,
      statusTab: {
        ...questionnaire.statusTab,
        lockOnSubmit: val
      },
    });
  }, [onChange, questionnaire]);

  const handleMoveOnSubmitChange = React.useCallback((val: boolean) => {
    onChange({
      ...questionnaire,
      statusTab: {
        ...questionnaire.statusTab,
        moveOnSubmit: val,
        submitTargetFolderId: val ? questionnaire.statusTab.submitTargetFolderId : undefined,
      },
    });
  }, [onChange, questionnaire]);

  const handleTargetFolderChange = React.useCallback((folderId: FolderPrimaryListDto['id']) => {
    onChange({
      ...questionnaire,
      statusTab: {
        ...questionnaire.statusTab,
        submitTargetFolderId: folderId,
      },
    });
  }, [onChange, questionnaire]);

  const handleRunRiskOnSubmitChange = React.useCallback((val: boolean) => {
    onChange({
      ...questionnaire,
      statusTab: {
        ...questionnaire.statusTab,
        runRisk: val,
      },
    });
  }, [onChange, questionnaire]);

  const openRiskSettings = React.useCallback(() => {
    setRiskSettingsOpened(true);
  }, []);

  const closeRiskSettings = React.useCallback(() => {
    setRiskSettingsOpened(false);
  }, []);

  const handleRiskSettingsChange = React.useCallback((riskSettings: QuestionnaireRiskSettings) => {
    onChange({
      ...questionnaire,
      statusTab: {
        ...questionnaire.statusTab,
        riskSettings,
      },
    });
    closeRiskSettings();
  }, [closeRiskSettings, onChange, questionnaire]);

  return (
    <div className="FhqDesignToolStatusActionsSubmittedQuestionnaires">
      <Title text={I18n.t('Submitted Questionnaires')} />
      <Subtitle
        text={I18n.t('Set the action to occur when the patient submits the questionnaire')}
      />
      <Checkbox 
        layout={FieldLayout.componentThenLabel}
        label={I18n.t('Lock on submit')}
        value={questionnaire.statusTab.lockOnSubmit}
        onChange={handleLockOnSubmitChange}
        metadata={{
          description: 'lock-on-submit'
        }}
      />
      <ConditionalField 
        label={I18n.t('Move on submit')}
        value={questionnaire.statusTab.moveOnSubmit}
        onChange={handleMoveOnSubmitChange}
        metadata={{
          description: 'move-on-submit'
        }}
      >
        <FolderPicker 
          errors={errors?.properties?.submitTargetFolder}
          required={questionnaire.statusTab.moveOnSubmit}
          labelWidth={140}
          value={questionnaire.statusTab.submitTargetFolderId ?? undefined}
          onChange={handleTargetFolderChange}
          metadata={{
            description: 'move-on-submit-folder',
          }}
          folders={folders}
          label={I18n.t('Choose Target Folder')}
        />
      </ConditionalField>
      <ConditionalField 
        label={I18n.t('Run risk on submit')}
        value={questionnaire.statusTab.runRisk}
        onChange={handleRunRiskOnSubmitChange}
        metadata={{
          description: 'run-risk-on-submit'
        }}
      >
        <Button 
          {...getTestAttributes(MODULE_NAME, 'edit-risk-settings', 'btn')}
          className="FhqDesignToolStatusActionsSubmittedQuestionnaires__edit-risk-button"
          onClick={openRiskSettings}
          text={I18n.t('Edit Risk Settings')}
        />
      </ConditionalField>
      {riskSettingsOpened && (
        <RiskSettings 
          onSubmit={handleRiskSettingsChange}
          riskSettings={questionnaire.statusTab.riskSettings ?? undefined}
          close={closeRiskSettings}
        />
      )}
    </div>
  );
};